<script setup lang="ts">
import useAsync from "~/hooks/use-async";
import useEventStore from "~/stores/event";
import EventsAPI from "~/services/events";
import { EventPlayerClipper } from "#components";
import getErrorLocalePathMessage from "~/utils/locale";

export interface CreateClipModalProps {
  mediaItem?: IModelEvent | null;
}

const props = defineProps<CreateClipModalProps>();

const { t, te } = useI18n();
const toaster = useToast();
const eventStore = useEventStore();

const clipData = reactive({
  name: "",
  targetId: props.mediaItem?.referenceId,
  url: props.mediaItem?.source.hd || "",
  startTime: 0,
  endTime: 0,
  streamName: "hd",
} as IRequestPayloadClipData);

const clipperRef = ref<InstanceType<typeof EventPlayerClipper> | null>(null);

const closeModal = () => (eventStore.isCreatingClip = false);
const onError = (error: Error) => {
  const { message } = getErrorLocalePathMessage(error.message, { t, te });
  return toaster.error(message) && closeModal();
};
const onSuccess = () => toaster.success(t("pages.events.tabs.event_videos.clip_creating_successfully_text")) && closeModal();
const { loading, run: createClip } = useAsync(EventsAPI.userMedia.createUserClip, { onSuccess, onError });

watch(() => clipperRef.value?.selectedRange, (range) => {
  if (!range)
    return;
  clipData.startTime = range[0];
  clipData.endTime = range[1];
});

onMounted(() => {
  nextTick(() => {
    eventStore.player?.trimAPI.setRange(eventStore.playerState?.currentTime ?? 0, eventStore.playerState?.currentTime ?? 40);
  });
});
onBeforeUnmount(() => eventStore.player?.trimAPI?.hide());
</script>

<template>
  <div class="createClipModal">
    <EventPlayerClipper ref="clipperRef" :start="clipData.startTime" :end="clipData.endTime" />

    <div class="createClipModal__container p-4">
      <div class="flex items-center justify-between">
        <h2 class="text-[var(--base-tab-text-color)] text-heading-3 text-center">
          {{ t("pages.events.tabs.event_videos.create_clip") }}
        </h2>

        <BaseButton
          type="icon"
          name="modal-close"
          :title="t('button.cancel')"
          @click.prevent="closeModal()"
        >
          <IconClose />
        </BaseButton>
      </div>

      <form @submit.prevent="createClip(clipData)">
        <BaseInput
          v-model="clipData.name"
          :label="t('pages.events.tabs.event_videos.name_the_clip')"
          label-class="text-heading-4"
          type="text"
          name="clip-modal"
          root-class="mt-6"
          required
          outline
        />
        <div class="flex w-full justify-between mt-6 gap-2">
          <BaseButton
            type="secondary"
            name="modal-close"
            :title="t('button.cancel')"
            class="w-1/2 text-button-bold text-main"
            @click="closeModal()"
          >
            {{ t("button.cancel") }}
          </BaseButton>
          <BaseButton
            type="primary"
            name="create-clip"
            :title="t('pages.events.tabs.event_videos.create_clip')"
            :disabled="loading"
            class="w-1/2 text-button-bold text-main"
          >
            <div class="flex items-center justify-center truncate">
              <IconClip :width="16" :height="16" fill="#ffffff" class="mx-2 flex-shrink-0" />
              {{ t("pages.events.tabs.event_videos.create_clip") }}
            </div>
          </BaseButton>
        </div>
      </form>
    </div>
  </div>
</template>

<style lang="scss">
.createClipModal {
  @apply w-full h-full;

  .createClipModal__container {
    @apply h-full;

    background: var(--base-card-bgColor);
  }
}
</style>
